import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { ArrowUpRightIcon } from 'lucide-react'
import { Box, Link, Stack, Typography, useTheme } from '@mui/material'

import { SURFACE_RADIUS } from '@repo/ui'

import { useReferrerState } from 'hooks'
import { useCertificateTriggerSettings } from 'hooks/api'
import {
  ACTIVATION_TRIGGERS,
  DEFAULT_ACTIVATION_TRIGGER,
} from 'constants/activationTriggers'
import { ROUTES } from 'routes'

interface InheritedActivationTriggerProps {
  companyId?: string | null
  /**
   * Tags in the package, i.e. how many certificates a bind would create. Undefined while the
   * package is still loading; 0 for a box that has not been filled yet.
   */
  tagCount?: number | null
}

/**
 * VRG-282 — the company's activation trigger, shown READ-ONLY at the point a package is bound
 * to a product, together with what binding will actually DO under it.
 *
 * Why it is here at all: binding creates certificates, and "will this bill me?" is the first
 * question that raises. The trigger already answers it, so the answer is surfaced rather than a
 * new billing concept invented.
 *
 * Why it now states the OUTCOME and not just the setting: activation is strictly inherited, so an
 * ON_CREATION company's bind creates active, billable certificates immediately. Naming the trigger
 * alone leaves the user to work that out from the count of tags in the box, the meaning of
 * "on creation", and what activation costs. The consequence belongs before the click, with the
 * real number in it.
 *
 * Why it is NOT settable here: a per-package override is VRG-283 and deliberately out of scope.
 * A second place to SET one company-wide value is how two surfaces drift into disagreeing, so
 * this reads the value and links to the one place that owns it.
 */
export const InheritedActivationTrigger = ({
  companyId,
  tagCount,
}: InheritedActivationTriggerProps) => {
  const theme = useTheme()
  // §42 — the destination renders "← Back to <here>" instead of stranding the user on Features.
  const referrer = useReferrerState()

  const { settings } = useCertificateTriggerSettings(companyId ?? '', {
    enabled: !!companyId,
  })

  // No settings row yet is not an error (the hook returns null on 404) — the company is on the
  // platform default until it saves one, so that is what gets shown.
  const current = settings?.activation_trigger ?? DEFAULT_ACTIVATION_TRIGGER
  const option =
    ACTIVATION_TRIGGERS.find(entry => entry.id === current) ??
    ACTIVATION_TRIGGERS.find(entry => entry.id === DEFAULT_ACTIVATION_TRIGGER)!

  const TriggerIcon = option.icon

  const activatesOnCreation = option.id === 'ON_CREATION'

  // An EMPTY box is the one case with no number to state: binding it records intent and the
  // certificates follow as tags are encoded in, so "creates 0 certificates" would be true and
  // useless. It gets its own sentence rather than a number.
  const count = typeof tagCount === 'number' && tagCount > 0 ? tagCount : null
  const plural = count === 1 ? '' : 's'

  // WHEN activation fires, in the reader's terms — read straight from the canonical option.
  // This used to override ON_PRIMARY_TRANSFER locally because the shared description named the
  // brand sending the transfer rather than the customer accepting it; the shared string is now
  // correct at its definition, so the override would only be a second place to keep in sync.
  const activationSentence = option.description

  // "one for each tag in the package" spells out where the number comes from — the count alone
  // left the reader to work out whether it was tags, boxes or line items.
  const outcome = count
    ? activatesOnCreation
      ? `Assigning this product creates and activates ${count} certificate${plural}: one for each tag in the package. Activation is what your plan bills, so these count towards your usage now. Active certificates cannot be re-pointed, so the package stays with the product you pick.`
      : `Assigning this product creates ${count} inactive certificate${plural}: one for each tag in the package. Nothing is billed at this point. ${activationSentence}`
    : activatesOnCreation
      ? 'This package has no tags in it yet. Each tag encoded into it becomes a certificate that is active straight away, and activation is what your plan bills.'
      : `This package has no tags in it yet. Each tag encoded into it becomes an inactive certificate. ${activationSentence}`

  return (
    <Box
      sx={{
        p: 3.5,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary[100]}`,
        borderRadius: `${SURFACE_RADIUS}px`,
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
        >
          <Stack direction="row" alignItems="center" spacing={2} minWidth={0}>
            <Typography variant="body3" color="text.secondary">
              Certificates activate
            </Typography>
            {/*
              The trigger's own glyph, from `ACTIVATION_TRIGGERS` — this row names
              one of the same three options the Features cards and the picker modal
              draw, so it carries the same symbol. Not a list, so there is no
              selection for it to signal; it is here purely so the value is
              recognisable at a glance rather than read.

              14px matches the ArrowUpRightIcon in the link opposite it, which is the
              only other glyph on this row.
            */}
            <TriggerIcon
              aria-hidden
              size={14}
              strokeWidth={1}
              color={theme.palette.text.secondary}
              style={{ flexShrink: 0 }}
            />
            <Typography variant="body3" fontWeight={500} noWrap>
              {option.title}
            </Typography>
          </Stack>

          <Link
            component={RouterLink}
            to={ROUTES.SETTINGS_COMPANY_FEATURES}
            state={referrer}
            underline="none"
            sx={{ flexShrink: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body4" fontWeight={500} color="text.primary">
                Features
              </Typography>
              <ArrowUpRightIcon size={14} strokeWidth={1.5} />
            </Stack>
          </Link>
        </Stack>

        {/* Line-height 1.6 rather than dense — this is explanatory copy, not a label. */}
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {outcome}
        </Typography>
      </Stack>
    </Box>
  )
}
